import axios from "axios"
import JwtService from "@/services/jwt.service"
import store from '@/store'
import LangHelper from "@/services/lang.helper"
import BranchHelper from "@/services/branch.helper"
import { getApiUrl } from "@/services/env.helper"

// Query string format of axios 0.21, which the backend expects: arrays as `key[]=a&key[]=b`, nested objects
// as a JSON string (`config={"curView":...}`). Since 0.28 axios sends nested objects as `config[curView]=...`
const encodeParam = val => encodeURIComponent(val)
  .replace(/%3A/gi, ':').replace(/%24/g, '$').replace(/%2C/gi, ',')
  .replace(/%20/g, '+').replace(/%5B/gi, '[').replace(/%5D/gi, ']')

const serializeParams = params => {
  if(params instanceof URLSearchParams) return params.toString()
  const parts = []
  Object.entries(params).forEach(([key, val]) => {
    if(val === null || typeof val === 'undefined') return
    if(Array.isArray(val)) key = `${key}[]`
    else val = [val]
    val.forEach(v => {
      if(v instanceof Date) v = v.toISOString()
      else if(v !== null && typeof v === 'object') v = JSON.stringify(v)
      parts.push(`${encodeParam(key)}=${encodeParam(v)}`)
    })
  })
  return parts.join('&')
}

// number of in-flight requests that show the global loader - it is hidden only when the last of them finishes,
// so one parallel request completing doesn't hide the loader while others are still running
let loadingRequests = 0
const finishLoading = config => {
  if(!config?.__countsForLoader) return
  config.__countsForLoader = false // guard against finishing the same request twice
  loadingRequests = Math.max(0, loadingRequests - 1)
  if(loadingRequests === 0) store.commit('setLoading', false)
}

const ApiService = {
  init(i18n, router, app) {
    axios.defaults.baseURL = getApiUrl()
    axios.defaults.paramsSerializer = serializeParams
    // so a hung request fails instead of keeping the global loader on forever; slow endpoints can pass their own `timeout`
    axios.defaults.timeout = 120000 // 2 minutes
    axios.interceptors.request.use((config) => {
      config.headers['Accept-Language'] = LangHelper.getLowcaseLanguage()
      let token = JwtService.getToken()
      config.headers['Authorization'] = token ? `Bearer ${token}` : `${process.env.VUE_APP_API_TOKEN}`
      if(['auth/login', 'auth/password/forgot', 'auth/password/reset', 'auth/password/requirements'].includes(config.url)) {
        config.headers['Authorization'] = process.env.VUE_APP_API_TOKEN
      }
      // remembers which token this request was actually sent with, so a 401 that comes back
      // after the user has since logged into a different session (a new token already saved)
      // can be recognized as belonging to the old, superseded session - see response handler
      config.__authTokenAtDispatch = token
      config.headers['From'] = BranchHelper.getBranch()
      if(config.loader !== false) {
        config.__countsForLoader = true
        loadingRequests++
        store.commit('setLoading', true)
      }
      return config
    }, error => Promise.reject(error))

    axios.interceptors.response.use((response) => {
      finishLoading(response.config)
      const appVersion = response.headers.app_version
      if(appVersion) JwtService.saveAppVersion(appVersion)
      return response
    }, function (error) {
      finishLoading(error.config)
      const status = error.response ? error.response.status : 500
      console.log(error.response)
      if(status === 422) {
        console.log(error.response.data)
        if(error.response.data.errors.show_message) {
          app.noty.error(error.response.data.errors.show_message[0])
        }
        store.commit('setValidationErrors', error.response.data.errors)
      }
      if(status === 405 || status === 403) {
        app.noty.error(i18n.global.t('not_enough_rights_for_this_operation'))
      }
      if(status === 406) {
        app.noty.error(i18n.global.t('your_tariff_plan_is_not_paid'))
      }
      if(status === 409) {
        app.noty.error(i18n.global.t('clinic_is_blocked'))
      }
      if(status === 500 && error?.response) {
        app.noty.error(i18n.global.t('server_error'))
      }
      if(status === 503) {
        router.push('/maintenance')
      }
      if(status === 423 && error.response.data.must_change_password) {
        // safety net for a stray call to some other endpoint made just before the
        // login/initial-driven redirect below takes effect - login() and the app's
        // initial bootstrap already handle this gracefully via a 200 response
        store.commit('setMustChangePassword', { user: store.state.auth.user })
        if(router.currentRoute.value.name !== 'ChangePassword') {
          router.push('/change-password')
        }
      }
      if(status === 401) {
        // only log out if this request was actually sent under the session that's still
        // active - a request dispatched under a since-superseded token (e.g. the user logged
        // into a different clinic while this request was still in flight) can 401 well after
        // the new, valid session's token has already been saved, and must not tear it down
        const requestMatchesCurrentSession = error.config?.__authTokenAtDispatch === JwtService.getToken()
        if(requestMatchesCurrentSession) {
          store.dispatch('logout').finally(() => {
            if(!JwtService.getToken()) router.push('/login') // not if a new session was started meanwhile
          })
        }
      }
      if(status === 400 && error.response.data.message) {
        app.noty.error(error.response.data.message?.message || error.response.data.message)
      }
      return Promise.reject(error)
    })
  },

  get(resource, params = {}, headers = {}) {
    let p = { params: params }
    p = {...p, ...headers }
    return axios.get(resource, p)
  },

  post(resource, params = {}, headers = {}, config = {}) {
    headers['Accept'] = 'application/json'
    config.headers = headers
    return axios.post(`${resource}`, params, config)
  },

  patch(resource, params = {}, headers = {}, config = {}) {
    headers['Accept'] = 'application/json'
    config.headers = headers
    return axios.patch(`${resource}`, params, config)
  },

  delete(resource, headers = {}, config = {}) {
    headers['Accept'] = 'application/json'
    config.headers = headers
    return axios.delete(`${resource}`, config)
  },
};

export default ApiService;
